import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Chip,
  CircularProgress, Alert, Tooltip, FormControlLabel, Checkbox, Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import apiClient from '@/api/client';
import dayjs from 'dayjs';

const MemberList = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
  const [sortModel, setSortModel] = useState([{ field: 'created_at', sort: 'desc' }]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedMember, setSelectedMember] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [moveOldToSecondary, setMoveOldToSecondary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        sort_by: sortModel[0]?.field || 'created_at',
        sort_order: sortModel[0]?.sort || 'desc',
        search: search || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
      };
      const res = await apiClient.get('/members', { params });
      setMembers(res.data.data.data);
      setTotalCount(res.data.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, sortModel, search, statusFilter]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleEdit = async (member) => {
    try {
      const res = await apiClient.get(`/members/${member.id}`);
      setSelectedMember(res.data.data);
      setFormData({
        first_name: res.data.data.first_name,
        last_name: res.data.data.last_name,
        email: res.data.data.email,
        mobile_number: res.data.data.mobile_number,
        wt_number: res.data.data.wt_number || '',
        notes: res.data.data.notes || '',
      });
      setMoveOldToSecondary(false);
      setError('');
      setEditOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleView = async (member) => {
    try {
      const res = await apiClient.get(`/members/${member.id}`);
      setSelectedMember(res.data.data);
      setViewOpen(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (member) => {
    setMemberToDelete(member);
    setDeleteError('');
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setSubmitting(true);
    setDeleteError('');
    try {
      await apiClient.delete(`/api/members/${memberToDelete.id}`);
      setDeleteConfirmOpen(false);
      fetchMembers();
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Delete failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await apiClient.put(`/api/members/${selectedMember.id}`, {
        ...formData,
        move_old_to_secondary: moveOldToSecondary,
      });
      setEditOpen(false);
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { field: 'member_no', headerName: 'Member No', width: 120 },
    { field: 'first_name', headerName: 'First Name', width: 130 },
    { field: 'last_name', headerName: 'Last Name', width: 130 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'mobile_number', headerName: 'Mobile', width: 130 },
    { field: 'wt_number', headerName: 'WhatsApp', width: 130 },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      valueFormatter: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.deleted_at ? 'Merged' : 'Active'}
          color={params.row.deleted_at ? 'default' : 'success'}
          size="small"
        />
      ),
    },
    {
      field: 'has_pending_conflict',
      headerName: 'Conflict',
      width: 100,
      renderCell: (params) => (
        params.value ? (
          <Tooltip title="Pending conflict exists">
            <WarningIcon color="warning" fontSize="small" />
          </Tooltip>
        ) : null
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)} title="Edit">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleView(params.row)} title="View">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row)} title="Delete" color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5">Members ({totalCount})</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Search"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 250 }}
              />
              <Button variant="outlined" onClick={fetchMembers} startIcon={<RefreshIcon />} disabled={loading}>
                Refresh
              </Button>
            </Box>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant={statusFilter === 'active' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setStatusFilter('active')}
            >
              Active
            </Button>
            <Button
              variant={statusFilter === 'merged' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setStatusFilter('merged')}
            >
              Merged
            </Button>
            <Button
              variant={statusFilter === 'all' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setStatusFilter('all')}
            >
              All
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ height: 'calc(100vh - 300px)', width: '100%' }}>
            <DataGrid
              rows={members}
              columns={columns}
              loading={loading}
              paginationMode="server"
              sortingMode="server"
              rowCount={totalCount}
              paginationModel={paginationModel}
              onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
              sortModel={sortModel}
              onSortModelChange={(newModel) => setSortModel(newModel)}
              pageSizeOptions={[15, 30, 50]}
              disableRowSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Member</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name || ''}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                margin="dense"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                margin="dense"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={formData.mobile_number || ''}
                onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                margin="dense"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="WhatsApp Number"
                value={formData.wt_number || ''}
                onChange={(e) => setFormData({ ...formData, wt_number: e.target.value })}
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                margin="dense"
              />
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Checkbox
                checked={moveOldToSecondary}
                onChange={(e) => setMoveOldToSecondary(e.target.checked)}
              />
            }
            label="Move old values to secondary contacts"
            sx={{ mt: 2 }}
          />
          {selectedMember?.contact_duplicates?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Existing Secondary Contacts:</Typography>
              {selectedMember.contact_duplicates.map(c => (
                <Chip
                  key={c.id}
                  label={`${c.contact_type}: ${c.contact_value}`}
                  size="small"
                  sx={{ m: 0.5 }}
                />
              ))}
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Modal */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Member Details</DialogTitle>
        <DialogContent dividers>
          {selectedMember && (
            <Grid container spacing={1}>
              <Grid item xs={6}><Typography variant="subtitle2">Member No:</Typography></Grid>
              <Grid item xs={6}><Typography>{selectedMember.member_no}</Typography></Grid>
              <Grid item xs={6}><Typography variant="subtitle2">First Name:</Typography></Grid>
              <Grid item xs={6}><Typography>{selectedMember.first_name}</Typography></Grid>
              <Grid item xs={6}><Typography variant="subtitle2">Last Name:</Typography></Grid>
              <Grid item xs={6}><Typography>{selectedMember.last_name}</Typography></Grid>
              <Grid item xs={6}><Typography variant="subtitle2">Email:</Typography></Grid>
              <Grid item xs={6}><Typography>{selectedMember.email}</Typography></Grid>
              <Grid item xs={6}><Typography variant="subtitle2">Mobile:</Typography></Grid>
              <Grid item xs={6}><Typography>{selectedMember.mobile_number}</Typography></Grid>
              <Grid item xs={6}><Typography variant="subtitle2">WhatsApp:</Typography></Grid>
              <Grid item xs={6}><Typography>{selectedMember.wt_number || '-'}</Typography></Grid>
              <Grid item xs={6}><Typography variant="subtitle2">Created At:</Typography></Grid>
              <Grid item xs={6}><Typography>{dayjs(selectedMember.created_at).format('YYYY-MM-DD HH:mm')}</Typography></Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Secondary Contacts:</Typography>
                {selectedMember.contact_duplicates?.length > 0 ? (
                  selectedMember.contact_duplicates.map(c => (
                    <Typography key={c.id} variant="body2">• {c.contact_type}: {c.contact_value}</Typography>
                  ))
                ) : <Typography variant="body2">None</Typography>}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete member "{memberToDelete?.first_name} {memberToDelete?.last_name}"?</Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberList;